import { Box, Button, Center, Loader, LoadingOverlay, SimpleGrid, Stack } from '@mantine/core';
import { FixedHeaderSection } from '@sportybot-web/components/layout/fixed-header-section/fixed-header-section';
import { Status } from '@sportybot-web/components/status/status';
import { AppMode } from '@sportybot-web/context/app-mode/context';
import { useAppMode } from '@sportybot-web/context/app-mode/use-app-mode';
import { PlayClipCard } from '@sportybot-web/features/clips/components/clip-card/play-clip-card/play-clip-card';
import { QuickCategories } from '@sportybot-web/features/search/components/quick-categories/quick-categories';
import { SearchFilters } from '@sportybot-web/features/search/components/search-filters/search-filters';
import { SearchInput } from '@sportybot-web/features/search/components/search-input/search-input';
import { SearchResultsHeader } from '@sportybot-web/features/search/components/search-results-header/search-results-header';
import { searchParamsSchema } from '@sportybot-web/features/search/schemas/search-params';
import { buildSearchQuery } from '@sportybot-web/features/search/utils/build-search-query';
import {
  type FilterSelection,
  type FilterValue,
  removeFilterValue,
} from '@sportybot-web/features/search/utils/filter-selection';
import { parseQueryInfo } from '@sportybot-web/features/search/utils/parse-query-info';
import { PLAY_CLIPS_RETURN_FIELDS } from '@sportybot-web/services/sporty-gateway/constants';
import { QueryType, SearchType } from '@sportybot-web/services/sporty-gateway/generated/graphql';
import {
  playClipsInfiniteQueryOptions,
  queryInfoQueryOptions,
} from '@sportybot-web/services/sporty-gateway/search/queries';
import { MotionStack } from '@sportybot-web/theme/animated';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { createFileRoute, stripSearchParams, useNavigate, useSearch } from '@tanstack/react-router';
import { compact } from 'es-toolkit';
import { SearchAlert, SearchX, X } from 'lucide-react';
import { AnimatePresence } from 'motion/react';

const DEFAULT_SEARCH_SORT = 'Timestamp DESC';

/* eslint-disable perfectionist/sort-objects */
export const Route = createFileRoute('/_authenticated/search')({
  component: RouteComponent,
  validateSearch: searchParamsSchema,
  search: {
    middlewares: [stripSearchParams({ qt: QueryType.STRUCTURED, q: '' })],
  },
});
/* eslint-enable perfectionist/sort-objects */

function RouteComponent() {
  const searchParams = useSearch({ from: Route.id });
  const navigate = useNavigate({ from: Route.fullPath });
  const { appMode } = useAppMode();

  const { q, qt } = searchParams;

  const {
    data: queryInfo,
    isError: isQueryInfoError,
    isLoading: isQueryInfoLoading,
    isSuccess: isQueryInfoSuccess,
    refetch: refetchQueryInfo,
  } = useQuery({
    ...queryInfoQueryOptions({ queries: [q], queryType: qt }),
    enabled: !!q.length,
    retry: 2,
    select: (data) => data.queryInfo?.[0] ?? null,
  });

  const isFreeTextSearch = qt === QueryType.FREETEXT;
  const isStructuredSearch = qt === QueryType.STRUCTURED;
  const filters = queryInfo?.valid ? parseQueryInfo(queryInfo.graph) : [];

  const {
    data: { plays, total } = { plays: [], total: 0 },
    fetchNextPage,
    hasNextPage,
    isError: isSearchError,
    isFetchingNextPage,
    isLoading: isSearchLoading,
    isRefetching: isSearchRefetching,
    isSuccess: isSearchSuccess,
    refetch: refetchPlays,
  } = useInfiniteQuery({
    ...playClipsInfiniteQueryOptions({
      limit: 24,
      query: isStructuredSearch
        ? `${q} ${PLAY_CLIPS_RETURN_FIELDS}`
        : `${queryInfo?.query} ${PLAY_CLIPS_RETURN_FIELDS}`,
      searchType: appMode === AppMode.MILB ? SearchType.MILB_UNIFIED : SearchType.UNIFIED,
    }),
    enabled: (isFreeTextSearch && !!queryInfo?.query) || (isStructuredSearch && !!q),
    select: (data) => {
      const plays = data.pages.flatMap((page) => page.search?.plays ?? []);
      const total = data.pages[0].search?.total ?? 0;
      return { plays, total };
    },
  });

  const handleNavigate = (search: typeof searchParams) => {
    void navigate({ search, to: Route.fullPath });
  };

  const handleSelectQuickCategory = (value: string) => {
    handleNavigate({ q: `${value} Order By ${DEFAULT_SEARCH_SORT}`, qt: QueryType.STRUCTURED });
  };

  const handleSubmitQuery = (nextQuery: string) => {
    if (!nextQuery) {
      handleNavigate({ q: '', qt: QueryType.STRUCTURED });
      return;
    }

    // Freetext search always replaces the URL query and forces FREETEXT.
    if (q !== nextQuery || qt !== QueryType.FREETEXT) {
      handleNavigate({ q: nextQuery, qt: QueryType.FREETEXT });
    } else {
      void refetchPlays();
    }
  };

  const handleRemoveFilter = (filterToUpdate: FilterSelection, valueToRemove: FilterValue) => {
    const nextFilters = removeFilterValue(filters, filterToUpdate, valueToRemove);

    if (!nextFilters.length) {
      handleSubmitQuery('');
      return;
    }

    handleNavigate({
      q: buildSearchQuery(nextFilters, DEFAULT_SEARCH_SORT),
      qt: QueryType.STRUCTURED,
    });
  };

  const isLoading = (isFreeTextSearch && isQueryInfoLoading) || isSearchLoading;
  const isEmpty =
    (isSearchSuccess && !plays.length) ||
    (isFreeTextSearch && isQueryInfoSuccess && !queryInfo?.valid);
  const isError = (isFreeTextSearch && isQueryInfoError) || isSearchError;

  return (
    <FixedHeaderSection>
      {/* Need stable height to prevent layout shifts during animations */}
      <Stack mih={122}>
        <SearchInput
          loading={isSearchRefetching}
          onSubmit={handleSubmitQuery}
          queryType={qt}
          value={q}
        />
        <QuickCategories
          onSelect={handleSelectQuickCategory}
          visible={!isSearchSuccess && !isError && !isLoading && !isEmpty}
        />
        <SearchResultsHeader
          loading={filters.length > 0 && isSearchLoading}
          total={total}
          visible={!!queryInfo?.graph || isSearchSuccess || isEmpty}>
          {filters.length > 0 && <SearchFilters filters={filters} onRemove={handleRemoveFilter} />}
          <Button
            color="default"
            fw="normal"
            onClick={() => handleSubmitQuery('')}
            rightSection={<X size={16} />}
            size="xs"
            type="reset"
            variant="default">
            Reset
          </Button>
        </SearchResultsHeader>
      </Stack>
      <Box h="100%" mih="0" pos="relative">
        <FixedHeaderSection.Content>
          {isLoading && (
            <Center data-testid="search-loading" h="100%">
              <Loader />
            </Center>
          )}

          {isEmpty && (
            <Status h="100%">
              <Status.Icon>
                <SearchX />
              </Status.Icon>
              <Status.Content>
                <Status.Title>Swing and a miss!</Status.Title>
                <Status.Description>
                  It looks like there are no results for your search.
                </Status.Description>
              </Status.Content>
            </Status>
          )}

          {isError && (
            <Status h="100%">
              <Status.Icon variant="error">
                <SearchAlert />
              </Status.Icon>
              <Status.Content>
                <Status.Title>We dropped the ball...</Status.Title>
                <Status.Description>
                  It looks like an error occurred while processing your search. Please try again.
                </Status.Description>
              </Status.Content>
              <Status.Actions>
                <Button
                  color="red"
                  onClick={() =>
                    void (isFreeTextSearch && isQueryInfoError
                      ? refetchQueryInfo()
                      : refetchPlays())
                  }
                  size="xs"
                  variant="outline">
                  Retry
                </Button>
              </Status.Actions>
            </Status>
          )}

          <AnimatePresence mode="popLayout">
            {isSearchSuccess && !isEmpty && (
              <MotionStack
                animate={{ opacity: 1 }}
                exit={{ opacity: 0, transition: { duration: 0.1 } }}
                initial={{ opacity: 0 }}
                key="search-results">
                <SimpleGrid data-testid="search-results-grid" minColWidth={500}>
                  {compact(plays).map((play) => (
                    <PlayClipCard key={play.id} play={play} />
                  ))}
                </SimpleGrid>
                {hasNextPage ? (
                  <Center>
                    <Button loading={isFetchingNextPage} onClick={() => void fetchNextPage()}>
                      See More
                    </Button>
                  </Center>
                ) : null}
              </MotionStack>
            )}
          </AnimatePresence>
        </FixedHeaderSection.Content>
        <LoadingOverlay
          data-testid="search-loading-overlay"
          overlayProps={{ blur: 2, radius: 'md' }}
          visible={isSearchRefetching}
          zIndex={1000}
        />
      </Box>
    </FixedHeaderSection>
  );
}
